import type {
    AnalyticsEvent,
    ContentLibraryUserInterfaceV2,
    ContentPathV1,
    CurrentPageContentPathV1,
} from "@khanacademy/event-schemas";

import type * as enums from "./enums.ts";

export type Conversion = Readonly<{
    id: ConversionName;
    extra?: Record<any, any>;
    event?: AnalyticsEvent;
}>;

export type ConversionName =
    | "activities_page_view"
    | "activity_overview_pageview"
    | "add_child_modal_click_back"
    | "add_child_modal_click_close"
    | "add_child_modal_click_next"
    | "add_child_modal_screen_shown"
    | "ai_guide_new_active_thread"
    | "alumni_page_view"
    | "badges_page_visit"
    | "cedar_event"
    | "child_does_not_have_email_address_click"
    | "class_creator_modal_click_close"
    | "class_creator_modal_click_next"
    | "class_creator_modal_click_skip"
    | "click_check_answer"
    | "click_related_video"
    | "cnc_chp_checklist_action"
    | "cnc_chp_checklist_change_selection"
    | "cnc_chp_checklist_close"
    | "cnc_chp_checklist_open"
    | "cnc_php_checklist_action"
    | "cnc_php_checklist_change_selection"
    | "cnc_php_checklist_close"
    | "cnc_php_checklist_open"
    | "coach_assignment_bulk_edit"
    | "coach_dashboard_clicked_class"
    | "coach_tools_csv_download"
    | "code_edited"
    | "condensed_tutorial_title_click"
    | "content_library_navigation_clicked"
    | "content_library_up_next_navigation_clicked"
    | "content_modal_navigate_to_page_through_title"
    | "content_modal_navigate_to_page"
    | "content_modal_next"
    | "content_modal_open"
    | "content_modal_prev"
    | "content_modal_view"
    | "content_shared_to_google_classroom"
    | "content_tab_preview_shown"
    | "create_child_error"
    | "create_child_success"
    | "demo_class_early_exit"
    | "demo_class_reminder_clicked"
    | "demo_class_subject_picker_shown"
    | "demo_class_subjects_selected"
    | "donation_banner_cc_processing_checkbox_on"
    | "donation_banner_option_clicked"
    | "donation_banner_viewed"
    | "drawing_area_stroke"
    | "drawing_area_toggle"
    | "exercise_submit_issue"
    | "getting_started_link_clicked"
    | "getting_started_shown"
    | "global_nav_choose_subject"
    | "global_nav_phantom_signin_clicked"
    | "gtp_click_overview_page_exam_cta"
    | "gtp_click_overview_page_exam_secondary_cta"
    | "gtp_expanded_help_module"
    | "gtp_page_view"
    | "gtp_review_problem"
    | "gtp_review_task"
    | "gtp_start_problem"
    | "gtp_view_checkin"
    | "guide_extension_page_view"
    | "guide_transcript_page_view"
    | "guide_transcripts_page_view"
    | "invite_child_error"
    | "invite_child_success"
    | "learnstorm_check_progress_click"
    | "learnstorm_dashboard_view"
    | "learnstorm_progress_reset"
    | "load_mission_dashboard"
    | "logged_in_homepage_assignment_modal_closed"
    | "logged_in_homepage_click_class_topic"
    | "logged_in_homepage_open_class_picker"
    | "logged_in_homepage_pick_classes"
    | "logged_in_homepage_pick_grade"
    | "logged_in_homepage_see_all_click"
    | "logged_in_homepage_visit"
    | "logged_out_homepage_visit"
    | "login_apple_button_clicked"
    | "login_classlink_button_clicked"
    | "login_clever_button_clicked"
    | "mastery_challenge_presented"
    | "mastery_challenge_resume_presented"
    | "missions_deprecation_banner_link_click"
    | "missions_deprecation_banner_shown"
    | "new_roster_modal_shown"
    | "old_progress_report_deprecation_banner_view"
    | "overview_click_tab"
    | "pageview_article"
    | "pageview_coding_challenge"
    | "pageview_coding_interactive"
    | "pageview_coding_project"
    | "pageview_coding_talkthrough"
    | "pageview_concept"
    | "pageview_course_challenge"
    | "pageview_course_discussion"
    | "pageview_domain"
    | "pageview_exercise"
    | "pageview_khanmigo_tool"
    | "pageview_khanmigo_teacher_tools_dashboard"
    | "pageview_login"
    | "pageview_mastery_challenge"
    | "pageview_myprogram"
    | "pageview_my_documents"
    | "pageview_newprogram"
    | "pageview_old_parent_dashboard"
    | "pageview_otherprogram"
    | "pageview_parent_dashboard"
    | "pageview_quiz"
    | "pageview_sat_dashboard_exams"
    | "pageview_sat_dashboard_overview"
    | "pageview_sat_dashboard_practice"
    | "pageview_sat_dashboard_tips_and_planning"
    | "pageview_search"
    | "pageview_signup"
    | "pageview_skill_check"
    | "pageview_subject"
    | "pageview_unit_test"
    | "pageview_video"
    | "parent_dashboard_click_create_child"
    | "parent_dashboard_click_how_to_create_child_account"
    | "parent_dashboard_click_onboarding"
    | "parent_dashboard_click_parent_resources"
    | "problem_specific_view"
    | "quiz_advance_past_start_card"
    | "quiz_start"
    | "request_child_error"
    | "request_child_success"
    | "reveal_answer_clicked"
    | "sat_landing_page_cta_click"
    | "sat_pageview_landing_page"
    | "sat_view_review_tab"
    | "school_picker_shown"
    | "set_mga_goal"
    | "signup_clever_button_clicked"
    | "skills_report_click_assign"
    | "skills_report_click_course_filter"
    | "skills_report_click_expand_skill"
    | "skills_report_click_expand_unit"
    | "skills_report_click_rit_band_filter"
    | "skills_report_click_student"
    | "skills_report_pageview"
    | "start_coach_task"
    | "start_mastery_task"
    | "start_personal_task"
    | "start_practice_task"
    | "start_system_task"
    | "started_problem"
    | "subject_progress_modal_closed"
    | "teacher_dashboard_click_course"
    | "teacher_dashboard_see_all_click"
    | "team_page_view"
    | "test_hoc_child"
    | "test_hoc_parent"
    | "test_inline_child"
    | "test_inline_parent"
    | "topic_page_click_floating_nav"
    | "topic_page_filter_all"
    | "topic_page_filter_article"
    | "topic_page_filter_practice"
    | "topic_page_filter_video"
    | "topic_progress_modal_closed"
    | "topic_progress_page_visit"
    | "topic_progress_sidebar_navigate"
    | "u13_reminder_modal_shown"
    | "unit_progress_skills_report_banner_view"
    | "up_next_cta_clicked"
    | "user_clicked_on_assignment"
    | "user_clicked_on_goal"
    | "user_clicked_on_learn_more_link"
    | "user_dismissed_kmap_welcome_banner"
    | "user_dismissed_multiple_accounts_banner"
    | "video_completed"
    | "video_end_card_cta"
    | "video_end_card_seen"
    | "video_end_card_signin_clicked"
    | "video_end_card_signin_seen"
    | "video_log_client"
    | "video_log"
    | "video_player_mounted"
    | "video_started_in_pageview"
    | "video_started"
    | "video_task_started"
    | "visited_individual_student_report_map_scores_tab"
    | "visited_mga_placement"
    | "web_phone_take_a_hint"
    | "writing_coach_stageview";

export type MarkConversionT = (
    conversion: Conversion | ReadonlyArray<Conversion>,
) => Promise<Response | void>;

export type PublishAnalyticsEventT = (
    event: AnalyticsEvent,
    legacyConversionName?: ConversionName,
) => Promise<Response | void>;

// A helper type that provides the set of payload fields found in events
// (excluding the type and version essentially).
export type EventPayload<T> = Omit<
    T, // These fields are not included in the event payload
    "eventType" | "eventSchemaVersion"
>;

/**
 * This is a type containing all the currentPageContentPath_ fields used in analytics events
 * (defined by the CurrentPageContentPath trait).
 */
export type CurrentPageContentPathFields =
    EventPayload<CurrentPageContentPathV1>;

/**
 * This is a type containing all the contentPath_ fields used in analytics events
 * (defined by the ContentPath trait).
 */
export type ContentPathFields = EventPayload<ContentPathV1>;

/**
 * This is a type containing all the contentLibraryUserInterfaceFields_ fields
 * used in analytics events (defined by the ContentLibraryUserInterface trait).
 */
export type ContentLibraryUserInterfaceFields =
    EventPayload<ContentLibraryUserInterfaceV2>;

// The types below were originally in shared-package/learing-time/types.ts

// NOTE(slim): When defining new conversion-esque types, always use $ReadOnly<T>
// and exact objects:
// - $ReadOnly<T> allows us to subtype the fields (so that `extra` can be different)
// - $Exact<T> helps to catch key errors associated with spreading updates in objects.

// Represents a significant user interaction with the site.
export type MajorConversionStub = Readonly<{
    conversion: string;
    timestamp: number;
    extraJson?: string;
    event?: AnalyticsEvent;
}>;

export type MajorConversion = Readonly<
    MajorConversionStub & {
        learningTimeInfo: LearningTimeInfo;
    }
>;

// Represents a BigBingo conversion that receives special handling and inherits
// extras from the previous major event, because it has limited access to
// information about the current conversion context.
//
// Minor conversions get transformed into major conversions, by inheriting
// LearningTimeInfo from the previous major conversion at the same URL.
export type MinorConversion = Readonly<{
    conversion: MinorConversionId;
    timestamp: number;
    extraJson?: string;
    event?: AnalyticsEvent;
}>;

export type MinorConversionData = Readonly<{
    id: MinorConversionId;
    extra?: Record<any, any>;
    event?: AnalyticsEvent;
}>;

// A guideline for adding to this list is that the conversion must be
// instrumented outside of the React component hierarchy (meaning you cannot
// fire it within a ConversionContext that has access to annotations from
// higher in the UI hierarchy). For more details on when to use minor
// conversions see: http://khanacademy.org/r/conversioncontext.
//
// All IDs here should be listed in flags/bigbingo/config.py.
export type MinorConversionId =
    | "activation_2.0_existing_account_back_clicked"
    | "activation_2.0_grassroots_different_class_clicked"
    | "activation_2.0_grassroots_login_clicked"
    | "activation_2.0_grassroots_signup_clicked"
    | "activation_2.0_join_code_click_continue"
    | "activation_2.0_link_create_instead"
    | "activation_2.0_link_dont_join"
    | "activation_2.0_link_personal_account"
    | "activation_2.0_login_back_clicked"
    | "activation_2.0_login_clicked"
    | "activation_2.0_school_email_back_clicked"
    | "activation_2.0_school_email_finish"
    | "activation_2.0_signup_back_clicked"
    | "activation_2.0_signup_clicked"
    | "activation_2.0_start_back_clicked"
    | "activation_2.0_start_google_button_clicked"
    | "activation_2.0_use_personal_account"
    | "activation_2.0_use_school_account"
    | "autocomplete"
    | "autocomplete_show_all"
    | "autocomplete_suggestion"
    | "auto_class_banner_shown"
    | "auto_class_banner_dismissed"
    | "auto_class_edited_name"
    | "cedar_event"
    | "content_discussions_view"
    | "content_exploration_banner_dismissed"
    | "content_tab_banner_detected_content_preview"
    | "content_tab_banner_rostering_upsell_cta_clicked"
    | "content_tab_banner_rostering_upsell_dismissed"
    | "content_tab_banner_rostering_upsell_shown"
    | "continue_with_google"
    | "donation_banner_viewed"
    | "exercise_submit_issue"
    | "fallback_player_error"
    | "global_nav_choose_subject"
    | "global_nav_choose_domain"
    | "global_nav_donate_clicked"
    | "global_nav_login_clicked"
    | "global_nav_open_learn_menu"
    | "global_nav_open_mobile"
    | "global_nav_open_notification"
    | "global_nav_open_notifications"
    | "global_nav_open_user_menu"
    | "global_nav_signup_clicked"
    | "global_nav_start_search"
    | "global_nav_visit_home"
    | "grfgl_banner_link_clicked"
    | "khankids_app_download"
    | "khankids_landing_page"
    | "last_chance_google_signup_clicked"
    | "learn_more_about_mastery_content_notifications"
    | "logged_out_districts_start_click"
    | "logged_out_donors_start_click"
    | "logged_out_mobile_start_click"
    | "logged_out_parent_start_click"
    | "logged_out_start_learning_click"
    | "logged_out_teacher_start_click"
    | "login_password_button_clicked"
    | "login_apple_button_clicked"
    | "login_facebook_button_clicked"
    | "login_google_button_clicked"
    | "mutate_dom_attempt"
    | "pagerequest"
    | "pageview"
    | "pageview_logged_in_homepage"
    | "pageview_logged_out_homepage"
    | "phantom_notification_closed"
    | "phantom_notification_clicked"
    | "phantom_notification_seen"
    | "phantom_notification_video_finished"
    | "phantom_notification_video_some"
    | "SEARCH_autocomplete_suggestion_searched"
    | "SEARCH_clicked_next"
    | "SEARCH_clicked_autocomplete_result"
    | "SEARCH_clicked_first_autocomplete_result"
    | "SEARCH_clicked_first_result"
    | "SEARCH_clicked_first_page_result"
    | "SEARCH_took_learning_action"
    | "SEARCH_viewed_google_results_JS"
    | "signup_email_button_clicked"
    | "signup_enter_class_code_clicked"
    | "signup_apple_button_clicked"
    | "signup_facebook_button_clicked"
    | "signup_login_link_clicked"
    | "signup_modal_shown"
    | "signup_birthdate_filled"
    | "signup_birthdate_focused"
    | "signup_error_username_invalid"
    | "signup_error_weak_password"
    | "signup_form_error_incomplete"
    | "signup_google_button_clicked"
    | "signup_switched_roles"
    | "skill_check_report_link_click"
    | "standard_click"
    | "subject_upsell_click"
    | "subject_upsell_view"
    | "teacher_campaign_cta_clicked"
    | "teacher_content_upsell_appear"
    | "teacher_content_upsell_dismiss_click"
    | "teacher_content_upsell_cta_click"
    | "teacher_content_upsell_student_response_click"
    | "teacher_content_upsell_teacher_response_click"
    | "teachers_page_hero_signup_cta_clicked"
    | "user_dismissed_new_mastery_content_notification"
    | "web_phone_click_related_video"
    | "web_phone_show_related_videos"
    | "ycla_button_click"
    | "ycla_join_button"
    | "ycla_share_button"
    | "ycla_start_learning_button"
    | "ycla_video_play"
    | "youtube_failed_fallback_to_html";

/******************************************************
 * Learning Time
 *****************************************************/

// TODO(slim): Consider compiling activity/learning_time.proto to JS at build
// time, and directly populating the protobuf.

// NOTE(slim): Technically all fields of protobufs are nullable, but we want
// our TypeScript types to reflect what client-side code should actually be doing.

// sync-start:learning-time-proto 321784443 https://github.com/Khan/webapp/blob/master/services/analytics/learning_time.proto

// ACHTUNG(slim): If you update this type, you probably need to update the
// PartialLearningTimeInfo type below!!
export type LearningTimeInfo = Readonly<{
    // The number of seconds offset from the learner UTC time to their
    // local time, to allow for in/out school detection.
    localTimeOffsetSeconds: {
        offsetSeconds: number;
    };
    // The full URL.  Intended for debugging/human-readability.  Code
    // should not be analyzing this field to do things! -- instead we
    // should be adding a new field to the protobuf that holds the
    // needed data.
    urlForDebugging: string;
    // What product is the learner using?  Test-prep, missions, etc.
    product?: enums.LearningTimeProduct;
    // Activity to which Learning Time will be credited.
    activity?: enums.LearningTimeActivity;
    // Associated content path, if one exists.
    content?: LearningTimeContent;
    // The classroom the student is currently working in (as well as info
    // about the specific assignment they're working on, if any).
    explicitClassroomContext?: LearningTimeExplicitClassroomContext;
    // Associated test prep info, if in a test prep product
    testPrep?: LearningTimeTestPrep;
    // Information about the task the user is currently working on, if any.
    // This could be a quiz, exercise, course challenge, or unit test.
    task?: LearningTimeTask;
    // Attributes of the user's interaction with the Khanmigo writing coach.
    writingCoach?: LearningTimeWritingCoach;
    // Attributes for user assessment activity.
    assessment?: LearningTimeAssessment;
    // The ka-locale that was in use for this request
    kaLocale: string;

    // The following structs are purposely omitted here (despite existing
    // in learning_time.proto) because they are populated on the server
    // (i.e. the analytics service):
    //    * classroom
    //    * schoolInfo
    //    * device
    //    * country
}>;

/**
 * Represents a partially-complete LearningTimeInfo struct.
 * This gets propagated down the component hierarchy, accumulating more
 * specific information as we know it.
 */
// ACHTUNG(slim): This should be kept in sync with the LearningTimeInfo type
// above! All fields are documented there.
export type PartialLearningTimeInfo = Readonly<{
    product?: enums.LearningTimeProduct;
    activity?: enums.LearningTimeActivity;
    content?: Partial<LearningTimeContent>;
    explicitClassroomContext?: Partial<LearningTimeExplicitClassroomContext>;
    testPrep?: Partial<LearningTimeTestPrep>;
    task?: Partial<LearningTimeTask>;
    writingCoach?: Partial<LearningTimeWritingCoach>;
    assessment?: Partial<LearningTimeAssessment>;
}>;

/**
 * This is the type of LearningTimeInfo expected by the server.
 */
export type LearningTimeInfo_ServerFormat = Readonly<
    Omit<LearningTimeInfo, "content" | "explicitClassroomContext"> & {
        // Associated content path, if one exists.
        content?: LearningTimeContent_ServerFormat;
        explicitClassroomContext?: LearningTimeExplicitClassroomContext_ServerFormat;
    }
>;

// TODO(slim): Make this type much more specific to define legal constructions
// of the topic tree.
// For example, if `contentId` is defined `contentTitle` should also be defined.
export interface LearningTimeContent {
    contentCommitSha: string;
    kind: enums.LearningTimeContentKind;
    domainId?: string;
    courseId?: string;
    unitId?: string;
    lessonId?: string;
    contentId?: string;
    contentTitle?: string;
    assessmentItemId?: string;
    exerciseId?: string;
    aiGuideThreadId?: string;
}

export type LearningTimeContent_ServerFormat = Omit<
    LearningTimeContent,
    "kind"
> & {
    kind: enums.LearningTimeContentKindServerFormat;
};

export interface LearningTimeExplicitClassroomContext {
    classroomDescriptor: string;
    assignment?: LearningTimeExplicitClassroomContextAssignment;
}

export interface LearningTimeExplicitClassroomContextAssignment {
    assignmentType: enums.LearningTimeAssignmentType;
    assignmentId: string;
}

export interface LearningTimeExplicitClassroomContext_ServerFormat {
    classroomDescriptor: string;
    assignmentType?: enums.LearningTimeAssignmentType;
    assignmentId?: string;
}

export interface LearningTimeTestPrep {
    examGroup: enums.LearningTimeExamGroup;
    exam?: enums.LearningTimeExam;
    areas?: ReadonlyArray<enums.LearningTimeArea>;
    // The lesson_id of the corresponding FrozenTopic for the being
    // worked on. Most the the test prep code is using slugs for this but
    // we are choosing to store the stable id instead.
    skillIds?: ReadonlyArray<string>;
}

export interface LearningTimeTask {
    // Unique key associated with the task. This is only available for exercises,
    // unit tests, quizzes, and course challenges. It's used to link mastery
    // level changes with learning time.
    taskKey?: string;
}

export interface LearningTimeWritingCoach {
    // The ID of the essay session that this activity is associated with.
    essaySessionId: string;

    // Section of the essay writing process that the student is working on.
    essayActivity: enums.LearningTimeEssayActivity;
}

export interface LearningTimeAssessment {
    // The assessment being taken.
    assessmentId: string;

    // The user assessment being taken. This will change if the user restarts the assessment.
    userAssessmentId: string;

    // The section the current question is in.
    sectionId: string;

    // The ID of the current question or task card.
    itemId: string;

    // What type of card is showing in the UI?
    cardType: enums.LearningTimeAssessmentCardType;

    eytPart?: enums.LearningTimeAssessmentEytPart;
}

/**
 * Define a 1:1 mapping between LearningTimeContentKind and
 * LearningTimeContentKindServerFormat.
 *
 * When you add a new type to enums.ContentKind, you need to add the
 * corresponding SCREAMING_SNAKE_CASE type to enums.ContentKindServerFormat,
 * then add both types to this map. The TypeScript assertions below will statically
 * validate these changes.
 */
export const mapContentKindToServerFormat = (
    from: enums.LearningTimeContentKind,
): enums.LearningTimeContentKindServerFormat => {
    const _contentKindServerFormat = {
        Topic: "TOPIC",
        Exercise: "EXERCISE",
        Article: "ARTICLE",
        Video: "VIDEO",

        Challenge: "CHALLENGE",
        Talkthrough: "TALKTHROUGH",
        Project: "PROJECT",
        Interactive: "INTERACTIVE",

        TopicQuiz: "TOPIC_QUIZ",
        TopicUnitTest: "TOPIC_UNIT_TEST",
        CourseChallenge: "COURSE_CHALLENGE",
        MasteryChallenge: "MASTERY_CHALLENGE",
    } as const;
    // Static check for soundness.
    // Ensures every key in `mapContentKindToServerFormat` is a valid
    // ContentKind, that is, no superfluous keys are defined.
    const _ = _contentKindServerFormat as Partial<
        Record<
            enums.LearningTimeContentKind,
            enums.LearningTimeContentKindServerFormat
        >
    >;
    return _contentKindServerFormat[from];
};

/**
 * Define a 1:1 mapping between LearningTimeExplicitClassroomContext and
 * LearningTimeExplicitClassroomContext_ServerFormat.
 */
export const mapExplicitClassroomContextToServerFormat = (
    from: LearningTimeExplicitClassroomContext,
): LearningTimeExplicitClassroomContext_ServerFormat => {
    return {
        classroomDescriptor: from.classroomDescriptor,
        assignmentType: from.assignment?.assignmentType,
        assignmentId: from.assignment?.assignmentId,
    };
};

// sync-end:learning-time-proto

/*********************************************************************
 * Redux
 * TODO(slim): Delete when we finally add redux and react-redux types.
 *********************************************************************/

export type Dispatcher<Action, State> = ((arg1: Action) => void) &
    ((
        arg1: ThunkAction<Action, State>,
    ) => ReturnType<ThunkAction<Action, State>>);

export type ThunkAction<Action, State> = (
    dispatch: Dispatcher<Action, State>,
    getState: () => State,
) => Promise<Response | null | undefined>;

export interface Store<Action, State> {
    dispatch: Dispatcher<Action, State>;
    getState: () => State;
}

export type QuizType =
    | "quiz"
    | "test"
    | "subject-challenge"
    | "mastery-challenge";
